import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ExternalLink, ShieldCheck, FileText, Cookie, Globe2, Building2, Info } from "lucide-react";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";

const JBH_PRIVACY = "https://jamesbassholding.com/legal.html#privacy-policy";
const JBH_GDPR = "https://jamesbassholding.com/legal.html#gdpr";
const CONTACT_EMAIL = "info@jamesbassholding.com";

const SECTIONS = [
  { id: "disclaimer", label: "What WorkOnIT is", icon: Info },
  { id: "terms", label: "Terms of Use", icon: FileText },
  { id: "privacy", label: "Privacy Policy", icon: ShieldCheck },
  { id: "gdpr", label: "GDPR & EU Privacy", icon: Globe2 },
  { id: "cookies", label: "Cookies", icon: Cookie },
  { id: "company", label: "Company & Contact", icon: Building2 },
];

function Section({ id, title, icon: Icon, children }) {
  return (
    <section id={id} data-testid={`legal-section-${id}`} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-300">
          <Icon size={18} />
        </div>
        <h2 className="font-display font-bold text-2xl text-slate-50">{title}</h2>
      </div>
      <div className="space-y-4 text-slate-300 text-sm leading-relaxed">{children}</div>
    </section>
  );
}

export default function Legal() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen wo-bg" data-testid="legal-page">
      <header className="sticky top-0 z-40 liquid-glass border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-teal-300 transition-colors">
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[240px_1fr] gap-10">
        {/* side nav */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-1">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3 px-3">Legal Center</p>
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`} data-testid={`legal-nav-${s.id}`}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-teal-300 hover:bg-white/5 transition-colors">
                <s.icon size={15} /> {s.label}
              </a>
            ))}
            <a href={JBH_PRIVACY} target="_blank" rel="noopener noreferrer" data-testid="legal-nav-jbh"
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-cyan-400 hover:text-cyan-300 hover:bg-white/5 transition-colors mt-2">
              <ExternalLink size={15} /> Full corporate policy
            </a>
          </div>
        </aside>

        <main className="min-w-0">
          <div className="mb-10">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-400 border border-cyan-500/25 rounded-full px-4 py-1.5 mb-5">
              <ShieldCheck size={12} /> Legal, Privacy & Terms
            </span>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-50 leading-tight">
              WorkOnIT Legal Center
            </h1>
            <p className="mt-4 text-slate-400 max-w-2xl">
              Please read these terms carefully. They explain what WorkOnIT is (and is not), how we handle your
              information, and the rights you have under applicable privacy laws including the EU GDPR.
            </p>
            <p className="mt-3 text-xs text-slate-500">Last updated: June 2026</p>
          </div>

          <div className="space-y-12">
            <Section id="disclaimer" title="What WorkOnIT is — and is not" icon={Info}>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
                <p className="text-amber-100 font-medium">
                  WorkOnIT is <strong>not</strong> an employment agency, recruitment agency, staffing firm, headhunter,
                  or labor broker.
                </p>
              </div>
              <p>
                WorkOnIT is an online <strong>directory and technology platform</strong> that helps people discover job
                opportunities and connect with companies, employers, and recruitment agencies. Our role is limited to
                providing the software, search tools (including the radar search), and directory listings that make these
                connections possible.
              </p>
              <p>Because we are a platform and not an agency, WorkOnIT does <strong>not</strong>:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>employ, represent, or place job seekers;</li>
                <li>act as an intermediary or party to any employment contract;</li>
                <li>conduct interviews, make hiring decisions, or negotiate salaries;</li>
                <li>guarantee employment, responses, interviews, or any particular outcome;</li>
                <li>verify, endorse, or take responsibility for job listings posted by third parties.</li>
              </ul>
              <p>
                All job listings, communications, applications, interviews, and hiring decisions are the sole
                responsibility of the respective employers, companies, and agencies. Any working relationship is strictly
                between the user and that third party. Users are responsible for verifying the legitimacy of any listing or
                company before sharing personal information or accepting an offer.
              </p>
            </Section>

            <Section id="terms" title="Terms of Use" icon={FileText}>
              <p><strong>1. Acceptance.</strong> By accessing or using WorkOnIT you agree to these Terms of Use and our Privacy Policy. If you do not agree, please do not use the platform.</p>
              <p><strong>2. Eligibility.</strong> You must be at least 18 years old (or the age of majority in your jurisdiction) and able to form a binding contract to use WorkOnIT.</p>
              <p><strong>3. Accounts.</strong> You are responsible for the accuracy of the information you provide and for keeping your credentials confidential. You are responsible for all activity under your account.</p>
              <p><strong>4. Acceptable use.</strong> You agree not to post false, misleading, unlawful, discriminatory, or infringing content; not to scrape, spam, or misuse the platform; and not to attempt to disrupt or gain unauthorized access to our systems or other users' data.</p>
              <p><strong>5. Directory role & no guarantee.</strong> As described above, WorkOnIT is a directory and platform, not an agency. We do not guarantee the availability, accuracy, or legitimacy of any listing, employer, agency, or applicant, and we do not guarantee employment or any outcome.</p>
              <p><strong>6. Third-party content & links.</strong> Listings and materials are provided by third parties and may link to external sites. We are not responsible for third-party content, practices, or policies.</p>
              <p><strong>7. Intellectual property.</strong> The WorkOnIT name, logo, software, and design are owned by JamesBass Technologies (a division of JamesBass Holding LLC). You retain rights to the content you submit but grant us a limited license to host and display it to operate the service.</p>
              <p><strong>8. Disclaimers & limitation of liability.</strong> The platform is provided "as is" without warranties of any kind. To the maximum extent permitted by law, WorkOnIT and JamesBass Holding LLC are not liable for indirect, incidental, or consequential damages, or for the acts or omissions of employers, agencies, or users.</p>
              <p><strong>9. Termination.</strong> We may suspend or terminate access at any time for violations of these terms or applicable law.</p>
              <p><strong>10. Governing law.</strong> These terms are governed by the laws of the State of California, United States, without regard to conflict-of-law principles.</p>
              <p><strong>11. Changes.</strong> We may update these terms from time to time. Continued use after changes take effect constitutes acceptance.</p>
            </Section>

            <Section id="privacy" title="Privacy Policy" icon={ShieldCheck}>
              <p>
                This summary explains how the WorkOnIT platform handles personal information. WorkOnIT is operated as part
                of JamesBass Holding LLC's technology services; the full corporate privacy policy applies in addition to
                this summary and is available here:{" "}
                <a href={JBH_PRIVACY} target="_blank" rel="noopener noreferrer" data-testid="legal-privacy-external-link"
                  className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1">
                  JamesBass Holding Privacy Policy <ExternalLink size={13} />
                </a>.
              </p>
              <p><strong>Information we collect:</strong> account details (name, email, role), profile and résumé/CV data you upload, job preferences and settings, saved jobs and applications, and technical/usage data such as IP address, device/browser, approximate location used for radar search, and cookies/local storage.</p>
              <p><strong>How we use it:</strong> to create and secure your account, power radar search and job matching, parse résumés you upload to help complete your profile, display listings and applications, communicate with you, and improve and protect the service.</p>
              <p><strong>Third-party processors:</strong> we rely on trusted providers to operate WorkOnIT, including cloud hosting and object storage, Google Sign-In (authentication), and AI/LLM providers used to parse résumé text. These providers process data under their own terms and appropriate safeguards.</p>
              <p><strong>Sharing:</strong> when you apply to a listing or make your profile public, relevant profile and application information is shared with the corresponding employer or agency. We do not sell your personal information for money.</p>
              <p><strong>Retention:</strong> we keep information for as long as needed to provide the service and meet legal obligations, after which it is deleted or anonymized.</p>
              <p><strong>Security:</strong> we use reasonable technical and organizational measures, though no system can be guaranteed completely secure.</p>
            </Section>

            <Section id="gdpr" title="GDPR & European Privacy Rights" icon={Globe2}>
              <p>
                If you are located in the European Economic Area, the United Kingdom, or another region with comparable
                laws, you have rights under the EU General Data Protection Regulation ("GDPR") and equivalent legislation.
              </p>
              <p>Subject to applicable conditions, you may request to:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>access the personal data we hold about you;</li>
                <li>correct inaccurate or incomplete data;</li>
                <li>erase your data ("right to be forgotten");</li>
                <li>restrict or object to certain processing;</li>
                <li>receive your data in a portable format (data portability);</li>
                <li>withdraw consent at any time, without affecting prior processing;</li>
                <li>lodge a complaint with your local data-protection authority.</li>
              </ul>
              <p><strong>Legal bases:</strong> we process personal data on the bases of your consent, performance of a contract (providing the platform), our legitimate interests in operating and improving the service, and compliance with legal obligations.</p>
              <p><strong>International transfers:</strong> where data is transferred outside the EEA/UK, we rely on legally recognized transfer mechanisms where required.</p>
              <p>
                To exercise any right, contact{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-400 hover:text-cyan-300 underline">{CONTACT_EMAIL}</a>. See also the{" "}
                <a href={JBH_GDPR} target="_blank" rel="noopener noreferrer" data-testid="legal-gdpr-external-link"
                  className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1">
                  full GDPR notice <ExternalLink size={13} />
                </a>.
              </p>
            </Section>

            <Section id="cookies" title="Cookies & Local Storage" icon={Cookie}>
              <p>WorkOnIT uses cookies and browser local storage to operate and improve the platform:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong>Essential:</strong> authentication token and session so you stay signed in.</li>
                <li><strong>Preferences:</strong> your chosen language and translation settings, saved radar city, and selected country.</li>
                <li><strong>Analytics & performance:</strong> to understand usage and improve the product.</li>
                <li><strong>Translation:</strong> when you select an additional European language, a Google Translate cookie is set to translate the interface.</li>
              </ul>
              <p>You can control cookies through your browser settings. Disabling some cookies may limit functionality.</p>
            </Section>

            <Section id="company" title="Company & Contact" icon={Building2}>
              <div className="rounded-xl liquid-glass p-5 space-y-2">
                <p className="text-slate-200">The WorkOnIT platform is <strong>managed by WorkOnIT</strong> and <strong>owned by JamesBass Technologies</strong>, Los Angeles, California — a division of <strong>JamesBass Holding LLC</strong>.</p>
                <p>JamesBass Holding LLC, California, United States.</p>
                <p>
                  Contact:{" "}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-400 hover:text-cyan-300 underline">{CONTACT_EMAIL}</a>
                </p>
                <p>
                  <a href={JBH_PRIVACY} target="_blank" rel="noopener noreferrer" data-testid="legal-company-external-link"
                    className="text-cyan-400 hover:text-cyan-300 underline inline-flex items-center gap-1">
                    View full corporate legal & privacy policy <ExternalLink size={13} />
                  </a>
                </p>
              </div>
            </Section>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
